import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Truck, Package, PackageCheck, Check, ShoppingBag, FileText } from "lucide-react";
import { api, getClientToken } from "../services/api";
import { useApp } from "../context/AppContext";
import { useToast } from "../hooks/use-toast";

const BACKEND = process.env.REACT_APP_BACKEND_URL; // eslint-disable-line no-unused-vars
const fmt = (n) => new Intl.NumberFormat("fr-DZ").format(Math.round(n || 0));
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const STEPS = ["Envoyé au fournisseur", "Reçu", "Préparé", "Livré"];
const STEP_ICON = { "Envoyé au fournisseur": Truck, "Reçu": Package, "Préparé": PackageCheck, "Livré": Check };

const ClientOrders = () => {
  const { client, catalog, addToCart, setCartOpen } = useApp();
  const { toast } = useToast();
  const [bcs, setBcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlId, setDlId] = useState("");

  const reorder = (b) => {
    let added = 0, missing = 0;
    (b.items || []).forEach((it) => {
      let p = null;
      if (it.productId) p = (catalog || []).find((x) => x.id === it.productId);
      if (!p) p = (catalog || []).find((x) => x.name && (x.name.fr === it.name || x.name === it.name));
      if (p) { addToCart(p.id, it.qty); added += 1; } else { missing += 1; }
    });
    if (added > 0) setCartOpen(true);
    if (added === 0) {
      toast({ title: "Ssuq", description: "Ces articles ne sont plus disponibles au catalogue." });
    } else if (missing > 0) {
      toast({ title: "Ssuq", description: `${added} article(s) ajouté(s) — ${missing} indisponible(s) au catalogue.` });
    }
  };

  const downloadInvoice = async (b) => {
    setDlId(b.id);
    try {
      const blob = await api.getInvoice(b.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Facture-${b.id.slice(0, 6).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Ssuq", description: "Facture indisponible. Réessayez ou reconnectez-vous." });
    } finally {
      setDlId("");
    }
  };

  const downloadAll = async () => {
    setDlId("all");
    try {
      const blob = await api.getAllInvoices();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Factures-Ssuq.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Ssuq", description: "Aucune facture à télécharger pour le moment." });
    } finally {
      setDlId("");
    }
  };

  const load = () => { setLoading(true); api.getClientOrders().then(setBcs).catch(() => setBcs([])).finally(() => setLoading(false)); };
  useEffect(() => { if (getClientToken()) load(); else setLoading(false); }, []);

  if (!client || !getClientToken()) {
    return (
      <div className="pt-28 pb-24 min-h-screen max-w-3xl mx-auto px-5 text-center">
        <ShoppingBag size={40} className="mx-auto text-orange" />
        <h1 className="font-display font-black text-3xl text-navy mt-4">Mes commandes</h1>
        <p className="text-gray-600 mt-3">Connectez-vous avec votre identifiant et votre code pour suivre vos commandes.</p>
        <Link to="/connexion" className="inline-block mt-6 btn-navy font-display font-semibold rounded-full px-6 py-3" data-testid="orders-login-link">Se connecter</Link>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24 min-h-screen" data-testid="client-orders-page">
      <div className="max-w-4xl mx-auto px-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <span className="inline-flex items-center gap-2 bg-navy/5 text-navy font-display font-semibold text-xs px-4 py-1.5 rounded-full"><span className="w-2 h-2 rounded-full bg-orange" /> {client.company}</span>
            <h1 className="font-display font-black text-4xl text-navy mt-3">Mes commandes</h1>
          </div>
          <button onClick={load} data-testid="orders-refresh" className="btn-outline font-display font-semibold text-sm rounded-full px-4 py-2 inline-flex items-center gap-2"><RefreshCw size={15} /> Actualiser</button>
        </div>

        {bcs.length > 0 && (
          <div className="mt-6 rounded-2xl border border-orange/30 bg-orange/5 p-4 flex items-center justify-between flex-wrap gap-3" data-testid="invoices-history">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange/15 flex items-center justify-center"><FileText size={18} className="text-orange" /></div>
              <div>
                <div className="font-display font-bold text-navy">Historique des factures</div>
                <div className="text-sm text-gray-500">Toutes vos factures ({bcs.length}) réunies dans un seul PDF.</div>
              </div>
            </div>
            <button onClick={downloadAll} disabled={dlId === "all"} data-testid="download-all-invoices" className="btn-navy font-display font-semibold text-sm rounded-full px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"><FileText size={15} /> {dlId === "all" ? "Préparation…" : "Télécharger toutes mes factures"}</button>
          </div>
        )}

        {loading ? (
          <p className="mt-10 text-gray-500 font-display">Chargement…</p>
        ) : bcs.length === 0 ? (
          <div className="mt-10 text-center text-gray-400 font-display py-16">Aucune commande pour le moment.<br /><Link to="/catalogue" className="text-orange font-semibold">Parcourir le catalogue →</Link></div>
        ) : (
          <div className="mt-8 space-y-4">
            {bcs.map((b) => {
              const idx = STEPS.indexOf(b.status);
              const late = new Date(b.deliveryDeadline) < new Date() && b.status !== "Livré";
              return (
                <div key={b.id} data-testid={`client-bc-${b.id}`} className="rounded-2xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-display font-bold text-navy">BC-{b.id.slice(0, 6).toUpperCase()} <span className="text-gray-400 font-normal text-sm">· {b.supplier}</span></div>
                      <div className="text-xs text-gray-400">{fmtDate(b.createdAt)}</div>
                    </div>
                    <span data-testid={`client-bc-status-${b.id}`} className={`inline-flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-full ${b.status === "Livré" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>{b.status}</span>
                  </div>

                  <div className="mt-4 grid grid-cols-4 gap-2">
                    {STEPS.map((s, i) => {
                      const Icon = STEP_ICON[s] || Truck;
                      const active = i <= idx;
                      return (
                        <div key={s} className="text-center">
                          <div className={`mx-auto w-9 h-9 rounded-full flex items-center justify-center ${active ? "bg-orange text-white" : "bg-gray-100 text-gray-400"}`}><Icon size={16} /></div>
                          <div className={`mt-1 text-[10px] font-display font-semibold ${active ? "text-navy" : "text-gray-400"}`}>{s}</div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 border-t border-gray-100 pt-3 space-y-1">
                    {(b.items || []).map((it, i) => (
                      <div key={i} className="flex justify-between text-sm"><span className="text-gray-700">{it.qty} × {it.name}</span><span className="font-semibold text-navy">{fmt(it.lineHT)} DA</span></div>
                    ))}
                    <div className="flex justify-between pt-1 border-t border-gray-100 mt-1"><span className="font-display font-bold text-navy">Total HT</span><span className="font-display font-black text-navy">{fmt(b.totalHT)} DA</span></div>
                  </div>
                  <div className={`mt-2 text-xs font-semibold ${late ? "text-red-600" : "text-gray-500"}`}>Livraison avant le {fmtDate(b.deliveryDeadline)}{late ? " (en retard)" : ""}{b.paymentMethod ? ` · Paiement : ${b.paymentMethod}` : ""}</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={() => downloadInvoice(b)} disabled={dlId === b.id} data-testid={`invoice-${b.id}`} className="btn-outline font-display font-semibold text-sm rounded-full px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"><FileText size={15} /> {dlId === b.id ? "…" : "Facture PDF"}</button>
                    <button onClick={() => reorder(b)} data-testid={`reorder-${b.id}`} className="btn-navy font-display font-semibold text-sm rounded-full px-4 py-2">Recommander</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientOrders;
